import { motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";

interface OnboardingStepHeaderProps {
  icon: LucideIcon;
  title: string;
  subtitle?: string;
  /** Tinted icon-tile variant: solid (primary bg) or soft (primary/20). Default soft. */
  variant?: "solid" | "soft";
  /** Add a brand-green glow under the icon (use on outcome moments). */
  glow?: boolean;
  /** Optional spinner-style rotation on the icon (active build steps). */
  spin?: boolean;
}

/**
 * Shared icon-tile + title + sub for the 4-step onboarding wizard.
 * Replaces the five ad-hoc <h1> blocks observed in the audit.
 */
export function OnboardingStepHeader({
  icon: Icon,
  title,
  subtitle,
  variant = "soft",
  glow = false,
  spin = false,
}: OnboardingStepHeaderProps) {
  const tile =
    variant === "solid"
      ? "bg-primary text-primary-foreground"
      : "bg-primary/20 text-primary";

  const iconEl = (
    <div
      className={`h-14 w-14 rounded-xl flex items-center justify-center mx-auto ${tile} ${
        glow ? "glow-primary" : ""
      }`}
    >
      <Icon className="h-7 w-7" />
    </div>
  );

  return (
    <div className="text-center space-y-3">
      {spin ? (
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          {iconEl}
        </motion.div>
      ) : (
        iconEl
      )}
      <h1 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight">
        {title}
      </h1>
      {subtitle && (
        <p className="text-sm text-muted-foreground max-w-sm mx-auto leading-relaxed">
          {subtitle}
        </p>
      )}
    </div>
  );
}
